// Enums
import { AppErrorEnumeration } from 'enumerations/app-error.enumeration';
import { ContractStatesEnumeration } from 'containers/contract/enumerations/contract-states.enumeration';
import { ContractConsentsTypeEnum } from 'containers/contract/types/contract-api-data-api-response.type';
// Resolvers
import {
  loadScreenDetailsResolver,
  saveScreenResolver,
} from '../resolvers/pre-contract.view.resolver';
// Translations
import {
  CONTINUE_PRE_CONTRACT,
  PRECONTRACTUAL_CONSENT,
} from './translations/pre-contract.view.handlers.translations';
// Types
import {
  CheckConsentHandlerType,
  ContinueToContractClickHandlerType,
  LoadScreenDetailsHandlerType,
  PreContractViewHandlerType,
  PreContractViewHandlerTypeBuilder,
  SetupContractButtonConfigurationHandlerType,
  UpdateContractReadHandlerType,
} from './types/pre-contract.view.handlers.types';
// Utils
import {
  checkIfAllConsentsAreCorrect,
  isConsentChecked,
  updateButtonWithDisabled,
} from '../../contractual/handlers/utils/contract.view.handlers.utils';
import { downloadFileAsBlob } from '@openbank/cf-ui-framework';
import { PreContractViewDataApiResponseType } from '../types/pre-contract-view-data-api-response.type';
import { isGilRiskError } from 'utils/resolver.utils';
import { navigateToCallbackUrl } from 'utils/app-callback-url.utils';

const continueToContractClickHandler = async ({
  acceptanceTimestamp,
  callbackUrl,
  consents,
  handleContinueButtonClickTracking,
  formatMessage,
  loanId,
  screenDetails,
  setError,
  setMainButtonConfiguration,
  setContractState,
}: ContinueToContractClickHandlerType): Promise<void> => {
  handleContinueButtonClickTracking();
  setMainButtonConfiguration(updateButtonWithDisabled(true));
  if (consents) {
    const saveParams = {
      acceptanceTimestamp,
      acceptedText: formatMessage({ id: PRECONTRACTUAL_CONSENT }),
      consents,
      documents: [{ documentId: screenDetails.document.id, scrollDown: true }],
    };
    const [{ errorCode = '' }, error] = await saveScreenResolver(saveParams);
    isGilRiskError(errorCode) && navigateToCallbackUrl({ callbackUrl, loanId });
    error
      ? setError(AppErrorEnumeration.SAVE_PRECONTRACT_FAILED_WITH_CONSENTS)
      : setContractState(ContractStatesEnumeration.CONTRACTUAL);
  } else {
    setError(AppErrorEnumeration.PRECONTRACT_FAILED_NO_CONSENTS);
  }
};

const saveByteCodeFromPrecontractual = ({
  response,
  setScreenDetails,
}: {
  response: PreContractViewDataApiResponseType;
  setScreenDetails: LoadScreenDetailsHandlerType['setScreenDetails'];
}) => {
  const { url, ...restDocument } = response.document;

  downloadFileAsBlob({
    url,
    callback: (byteCode: Blob): void => {
      setScreenDetails({
        document: { ...restDocument, url, byteCode },
        consents: response.consents,
      });
    },
  });
};

const loadScreenDetailsHandler = async ({
  setError,
  setScreenDetails,
  setConsents,
}: LoadScreenDetailsHandlerType): Promise<void> => {
  const [response, error] = await loadScreenDetailsResolver();

  if (error) {
    setError(AppErrorEnumeration.PRECONTRACT_SCREEN_DETAILS_FAILED);
  } else if (response) {
    saveByteCodeFromPrecontractual({ response, setScreenDetails });
    setConsents(
      response?.consents.map(consent => ({
        type: consent.type,
        checked: false,
        mandatory: consent.mandatory,
      }))
    );
  }
};

const setupContractButtonConfigurationHandler = ({
  acceptanceTimestamp,
  callbackUrl,
  consents,
  formatMessage,
  handleContinueButtonClickTracking,
  loanId,
  screenDetails,
  setMainButtonConfiguration,
  setError,
  setContractState,
}: SetupContractButtonConfigurationHandlerType): void => {
  if (consents) {
    setMainButtonConfiguration({
      children: formatMessage({ id: CONTINUE_PRE_CONTRACT }),
      disabled: !checkIfAllConsentsAreCorrect(consents),
      type: 'PRIMARY',
      size: 'LARGE',
      onClick: () =>
        continueToContractClickHandler({
          acceptanceTimestamp,
          callbackUrl,
          consents,
          formatMessage,
          loanId,
          screenDetails,
          setError,
          setContractState,
          setMainButtonConfiguration,
          handleContinueButtonClickTracking,
        }),
    });
  }
};

const checkConsentHandler = ({
  acceptanceTimestamp,
  consent,
  consents,
  setAcceptanceTimestamp,
  setConsents,
  setMainButtonConfiguration,
  handleConsentsCheckboxChangeTracking,
}: CheckConsentHandlerType) => {
  if (consents && setConsents) {
    const updatedConsents = consents.map(consentIteration =>
      consentIteration.type === consent
        ? { ...consentIteration, checked: !consentIteration.checked }
        : consentIteration
    );

    handleConsentsCheckboxChangeTracking({
      checked: isConsentChecked({ updatedConsents, consent }),
      type: consent,
    });

    setConsents(updatedConsents);
    setMainButtonConfiguration(updateButtonWithDisabled(checkIfAllConsentsAreCorrect(consents)));
    !acceptanceTimestamp && setAcceptanceTimestamp(new Date().toISOString());
  }
};

const setPreContractReadHandler = ({ setIsPreContractRead }: UpdateContractReadHandlerType): void =>
  setIsPreContractRead(true);

const setPreContractualDocumentRenderedEventHandler = ({
  setIsDocumentRendered,
}: Pick<PreContractViewHandlerTypeBuilder, 'setIsDocumentRendered'>): void => {
  setIsDocumentRendered(true);
};

const PreContractViewHandlers = ({
  setIsDocumentRendered,
  ...props
}: PreContractViewHandlerTypeBuilder): PreContractViewHandlerType => ({
  handleSetupPreContractButtonConfiguration: () => setupContractButtonConfigurationHandler(props),
  handleLoadScreenDetails: () => loadScreenDetailsHandler(props),
  handleCheckConsent: (consent: ContractConsentsTypeEnum) =>
    checkConsentHandler({ ...props, consent }),
  handlePreContractRead: () => setPreContractReadHandler(props),
  handleSetPreContractualDocumentRenderedEvent: () =>
    setPreContractualDocumentRenderedEventHandler({ setIsDocumentRendered }),
});

export { PreContractViewHandlers };
